import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "../api";
import StickyHead from "../StickyHead";

const TYPES = [
  ["CUSTOMER", "고객사"],
  ["VENDOR", "매입처"],
  ["PARTNER", "협력사"],
  ["FREELANCER", "프리랜서"],
  ["GOV", "기관"],
  ["INTERNAL", "내부(I.META)"],
];

const EMPTY = {
  name: "",
  type: "CUSTOMER",
  biz_reg_no: "",
  representative: "",
  biz_condition: "",
  biz_item: "",
  address: "",
  note: "",
};

export default function CompanyForm() {
  const { id } = useParams(); // 있으면 수정 모드
  const editing = Boolean(id);
  const [form, setForm] = useState(EMPTY);
  const [bizRegFile, setBizRegFile] = useState(null); // 사업자등록증
  const [bankFile, setBankFile] = useState(null); // 통장사본
  const [error, setError] = useState("");
  const [warn, setWarn] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(editing);
  const navigate = useNavigate();

  useEffect(() => {
    if (!editing) return;
    (async () => {
      try {
        const c = await api.get(`/companies/${id}`);
        setForm({
          name: c.name || "",
          type: c.type || "CUSTOMER",
          biz_reg_no: c.biz_reg_no || "",
          representative: c.representative || "",
          biz_condition: c.biz_condition || "",
          biz_item: c.biz_item || "",
          address: c.address || "",
          note: c.note || "",
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [editing, id]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function uploadDoc(companyId, file, docType, title) {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", docType);
    fd.append("title", title);
    await api.upload(`/companies/${companyId}/documents`, fd);
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (busy) return;
    if (!form.name.trim()) {
      setError("업체명은 필수입니다.");
      return;
    }
    setBusy(true);
    setError("");
    setWarn("");
    try {
      if (editing) {
        await api.post(`/companies/${id}`, form);
        navigate(`/sstoa/companies/${id}`, { replace: true });
        return;
      }

      // 신규 등록: 먼저 업체 생성, 이어서 첨부 파일 업로드
      const created = await api.post("/companies", form);

      const failed = [];
      if (bizRegFile) {
        try {
          await uploadDoc(created.id, bizRegFile, "BIZ_REG", "사업자등록증");
        } catch {
          failed.push("사업자등록증");
        }
      }
      if (bankFile) {
        try {
          await uploadDoc(created.id, bankFile, "BANKBOOK", "통장사본");
        } catch {
          failed.push("통장사본");
        }
      }

      if (failed.length) {
        // 업체는 생성됐으므로 상세로 이동하되 경고를 남긴다
        setWarn(`${failed.join(", ")} 업로드에 실패했습니다. 상세 화면에서 다시 올려주세요.`);
      }
      navigate(`/sstoa/companies/${created.id}`, { replace: true });
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  if (loading) return <div className="sstoa-empty">불러오는 중…</div>;

  return (
    <div>
      <StickyHead>
        <div style={{ marginBottom: 12 }}>
          <Link to={editing ? `/sstoa/companies/${id}` : "/sstoa/companies"} className="sstoa-back">← 돌아가기</Link>
        </div>
        <h1 className="sstoa-title" style={{ marginBottom: 20 }}>
          {editing ? "업체 수정" : "업체 등록"}
        </h1>
      </StickyHead>

      {error && <div className="sstoa-error" style={{ marginBottom: 16, maxWidth: 560 }}>{error}</div>}
      {warn && <div className="sstoa-error" style={{ marginBottom: 16, maxWidth: 560, background: "#fff6e6", borderColor: "#f0d9a8", color: "#8a6d1f" }}>{warn}</div>}

      <form className="sstoa-form" onSubmit={onSubmit}>
        <div className="sstoa-row">
          <div className="sstoa-field">
            <label>업체명 <span className="req">*</span></label>
            <input className="sstoa-input" value={form.name} onChange={upd("name")} disabled={busy} />
          </div>
          <div className="sstoa-field">
            <label>구분</label>
            <select className="sstoa-select" value={form.type} onChange={upd("type")} disabled={busy}>
              {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>

        <div className="sstoa-row">
          <div className="sstoa-field">
            <label>사업자등록번호</label>
            <input className="sstoa-input" value={form.biz_reg_no} onChange={upd("biz_reg_no")} disabled={busy} />
          </div>
          <div className="sstoa-field">
            <label>대표자</label>
            <input className="sstoa-input" value={form.representative} onChange={upd("representative")} disabled={busy} />
          </div>
        </div>

        <div className="sstoa-row">
          <div className="sstoa-field">
            <label>업태</label>
            <input className="sstoa-input" value={form.biz_condition} onChange={upd("biz_condition")} disabled={busy} />
          </div>
          <div className="sstoa-field">
            <label>업종</label>
            <input className="sstoa-input" value={form.biz_item} onChange={upd("biz_item")} disabled={busy} />
          </div>
        </div>

        <div className="sstoa-field">
          <label>주소</label>
          <input className="sstoa-input" value={form.address} onChange={upd("address")} disabled={busy} />
        </div>

        <div className="sstoa-field">
          <label>메모</label>
          <textarea className="sstoa-textarea" value={form.note} onChange={upd("note")} disabled={busy} />
        </div>

        {!editing && (
          <div className="sstoa-panel sstoa-panel--pad">
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--s-fg-dim)", marginBottom: 12 }}>
              첨부 서류 (선택)
            </div>
            <div className="sstoa-row">
              <div className="sstoa-field">
                <label>사업자등록증</label>
                <input className="sstoa-input" type="file" onChange={(e) => setBizRegFile(e.target.files[0] || null)} disabled={busy} />
              </div>
              <div className="sstoa-field">
                <label>통장사본</label>
                <input className="sstoa-input" type="file" onChange={(e) => setBankFile(e.target.files[0] || null)} disabled={busy} />
              </div>
            </div>
            <p style={{ margin: "10px 0 0", fontSize: 12, color: "var(--s-fg-mute)" }}>
              PDF·이미지·한글/오피스 문서, 파일당 최대 20MB. 등록 후 상세 화면에서 더 추가할 수 있습니다.
            </p>
          </div>
        )}

        <div className="sstoa-actions">
          <button type="submit" className="sstoa-btn sstoa-btn--primary" disabled={busy}>
            {busy ? "저장 중…" : "저장"}
          </button>
          <Link to={editing ? `/sstoa/companies/${id}` : "/sstoa/companies"} className="sstoa-btn">취소</Link>
        </div>
      </form>
    </div>
  );
}
